import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import { motion } from "framer-motion";
import { Cookies, getCookieConsentValue } from "react-cookie-consent";
import { colors } from "../../theme/colors";

const MotionBox = motion(Box);

const COOKIE_NAME = "ouro-matchmaking-consent";

function persistConsent(value) {
    const options = {
        expires: 365,
        sameSite: "lax",
        secure: typeof window !== "undefined" && window.location.protocol === "https:",
    };

    Cookies.set(`${COOKIE_NAME}-legacy`, value, options);
    Cookies.set(COOKIE_NAME, value, options);
}

function shouldShowBanner() {
    if (typeof window === "undefined") {
        return false;
    }

    return getCookieConsentValue(COOKIE_NAME) === undefined;
}

export default function CookieConsentBanner() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        setVisible(shouldShowBanner());
    }, []);

    const handleAccept = () => {
        persistConsent("true");
        setVisible(false);
    };

    if (!visible) {
        return null;
    }

    return (
        <MotionBox
            role="dialog"
            aria-labelledby="cookie-consent-title"
            aria-describedby="cookie-consent-description"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            sx={{
                position: "fixed",
                left: { xs: 16, sm: 24 },
                right: { xs: 16, sm: 24 },
                bottom: { xs: 16, sm: 24 },
                width: "auto",
                maxWidth: "1180px",
                margin: "0 auto",
                zIndex: 9999,
                pointerEvents: "auto",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    background: alpha(colors.background.card, 0.96),
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    border: `1px solid ${alpha(colors.primary.main, 0.18)}`,
                    borderRadius: "18px",
                    boxShadow: `
                        0 35px 80px rgba(0,0,0,0.55),
                        inset 0 1px rgba(255,255,255,0.03)
                    `,
                    px: { xs: "16px", sm: "24px" },
                    py: { xs: "14px", sm: "16px" },
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        background: `
                            radial-gradient(
                                ellipse at center,
                                rgba(232,163,61,0.07),
                                transparent 70%
                            )
                        `,
                        pointerEvents: "none",
                    },
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "stretch", md: "center" },
                        justifyContent: "space-between",
                        gap: { xs: 1.25, md: 2 },
                        minHeight: { md: "78px" },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 1.25, sm: 1.5 },
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                background: colors.accent.subtle,
                                border: `1px solid ${alpha(colors.primary.main, 0.28)}`,
                                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
                            }}
                        >
                            <CookieOutlinedIcon
                                sx={{ color: colors.primary.main, fontSize: "1.35rem" }}
                            />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                id="cookie-consent-title"
                                sx={{
                                    fontSize: "1rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.02em",
                                    color: colors.text.primary,
                                    lineHeight: 1.2,
                                }}
                            >
                                Cookies & Privacy
                            </Typography>
                            <Typography
                                id="cookie-consent-description"
                                sx={{
                                    mt: "0.2rem",
                                    fontSize: "0.82rem",
                                    lineHeight: 1.55,
                                    color: colors.text.secondary,
                                    maxWidth: "560px",
                                }}
                            >
                                We use cookies to improve your experience and analyze site traffic.
                            </Typography>
                            <Box
                                component={Link}
                                to="/privacy"
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    mt: "0.35rem",
                                    color: colors.primary.main,
                                    fontSize: "0.82rem",
                                    fontWeight: 600,
                                    textDecoration: "none",
                                    transition: "transform 0.25s ease, color 0.25s ease",
                                    "&:hover": {
                                        color: colors.accent.soft,
                                        transform: "translateX(2px)",
                                    },
                                }}
                            >
                                Privacy Policy →
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: { xs: "stretch", sm: "flex-end" },
                            flexShrink: 0,
                            width: { xs: "100%", md: "auto" },
                        }}
                    >
                        <Button
                            type="button"
                            onClick={handleAccept}
                            sx={{
                                height: 44,
                                px: "26px",
                                borderRadius: "12px",
                                textTransform: "none",
                                fontSize: "0.875rem",
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                                color: colors.text.contrast,
                                background: "linear-gradient(180deg, #F4BE5A, #D9932C)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                boxShadow: "0 10px 22px rgba(232,163,61,0.18)",
                                transition: `
                                    transform 0.25s ease,
                                    box-shadow 0.25s ease,
                                    filter 0.25s ease
                                `,
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 18px 35px rgba(232,163,61,0.35)",
                                    filter: "brightness(1.05)",
                                },
                                "&:active": {
                                    transform: "translateY(0)",
                                    boxShadow: "0 8px 16px rgba(232,163,61,0.22)",
                                },
                            }}
                        >
                            Accept
                        </Button>
                    </Box>
                </Box>
            </Box>
        </MotionBox>
    );
}
